<!--
	Wrapper component: renderless. The script owns state and requests; the template only
	exposes them through a scoped slot and renders no markup of its own. Twig owns every
	line of markup inside <review-votes …> and binds to the slot props with v-slot="{ … }",
	${ }, :class, :disabled, and @click. Inputs arrive as kebab-case attributes on the tag.
-->
<template>
	<slot v-bind="{ upvotes, downvotes, hasUp, hasDown, busy, error, cast }" />
</template>

<script setup>
import { computed, ref } from "vue";
import { postAction } from "@composables/api";

const props = defineProps({
	reviewId: {
		type: Number,
		required: true,
	},
	initialUp: {
		type: Number,
		default: 0,
	},
	initialDown: {
		type: Number,
		default: 0,
	},
	// The current visitor's Vote: "up", "down", or "" for none
	initialVote: {
		type: String,
		default: "",
	},
	canVote: {
		type: Boolean,
		default: false,
	},
});

const upvotes = ref(props.initialUp);
const downvotes = ref(props.initialDown);
const vote = ref(props.initialVote);
const busy = ref(false);
const error = ref(null);

const hasUp = computed(() => vote.value === "up");
const hasDown = computed(() => vote.value === "down");

async function cast(type) {
	if (!props.canVote || busy.value) {
		return;
	}

	busy.value = true;
	error.value = null;

	try {
		const { data } = await postAction("site-module/review-vote/vote", {
			// The controller reads commentId; a Review is a top-level comment
			commentId: props.reviewId,
			type,
		});

		// The action answers 200 with success: false when it refuses a Vote
		if (!data?.success) {
			throw new Error(data?.error || "Could not record your vote.");
		}

		upvotes.value = data.upvotes;
		downvotes.value = data.downvotes;

		// Mirror the controller's toggle: the same type again clears the Vote, the other type switches it
		vote.value = vote.value === type ? "" : type;
	} catch (e) {
		error.value = e.message;
	} finally {
		busy.value = false;
	}
}
</script>
